// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

//User info
type userInfo = {
  userId: number;
  schoolId: number;
  firstName: string;
  lastName: string;
  permissionLevel: string;
  avatarId: number;
  preferences: userPrefs;
};

//User preferences
type userPrefs = {
  ideLang: string;
  siteLang: string;
  programmingStyle: string;
  soundEnabled: boolean;
  musicEnabled: boolean;
};

//Statistics regarding the user
type stats = {
  deaths: number;
  bronzeMedals: number;
  silverMedals: number;
  goldMedals: number;
  achievements: Record<string, boolean>;
};

//General information about a level
type level = {
  levelId: number;
  name: string;
  world: string;
  locked: boolean;
  medals: number;
  preview: string;
};

//Level preview
type levelPreview = {
  id: number;
  name: string;
};

// General information about a bundle
type bundle = {
  bundleId: number;
  name: string;
  lock: boolean;
  published: boolean;
};

// The amount of medals a user has earned
type medals = {
  bronze: number;
  silver: number;
  gold: number;
  super: number;
};

type achievement = {
  achievementId: number;
  slug: string;
  name: string;
  description: string;
  world: string;
  earned: boolean;
};

// The achievements
type achievements = achievement[];

//PSD stack entry
interface PSDStackEntry {
  type: string;
}

//An entry in the undo-redo stack for the PSD
interface PSDStackEntryMove extends PSDStackEntry {
  prevParent: Element;
  nextParent: Element | null;
  childNo: number;
  nextChildNo: number;
  movedElem: Element;
}

//An entry in the undo-redo stack for the PSD
interface PSDStackEntrySwitch extends PSDStackEntry {
  toggleEvent: Event;
  value: string;
  constElem: HTMLInputElement | HTMLSelectElement | null;
}

//An entry in the undo-redo stack for the PSD
interface PSDStackEntryValue extends PSDStackEntry {
  oldValue: string;
  newValue: string;
  constElem: HTMLInputElement | HTMLSelectElement;
}

//General information about a bundle
type bundleLevelSettings = {
  bundleId: number;
  levelId: number;
  index: number;
  name: string;
  allowPsd: boolean;
  allowWritten: boolean;
};

//Information about a specific level
type levelSettings = {
  levelId: number;
  allowPsd: boolean;
  allowWritten: boolean;
};

//Level state
type levelState = {
  id: number;
  bundleId: number;
  tiles: tile[];
  entities: entity[];
  playerControlable: playableCharacter;
};

//Level statistics of a student
type levelStatsStudent = {
  worldType: string;
  worldSpecificStats: {
    statName: string;
    type: string;
    value: number;
  }[];
  avarageAttempts: number;
  totalAttempts: number;
  avarageTimesOpened: number;
  totalTimesOpened: number;
  avarageDuration: number;
  totalDuration: number;
  percentageMemory: number;
  medalObtained: string;
  percentageSuccessfullRun: number;
  amountWorkingSolution: number;
  amountAnySolution: number;
  numberOfAdvancements: number;
  amountUsedLanguages: { lang: string; amount: number }[];
};

//Progress of level bundle
type levelBundleProgress = {
  bundleId: number;
  levelId: number;
  userId: number;
  medal: number;
  solution: string;
};

//Decoded JWT token
type JWTToken = {
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  nbf: number;
  role: string;
  unique_name: string;
};

//Saved level
type levelSave = {
  bundleId: number;
  levelId: number;
  code: string;
};

type SavePacket = {
  //type: string;
  variableList?: varType[];
  code: expression | string;
};

type SaveObject = {
  PSDSave: SavePacket;
  typedSave: SavePacket;
};

//Level info
type levelInfo = {
  levelId: number;
  userId: string;
  name: string;
  levelData: levelData;
  world: string;
  preview: string;
  seed: number;
  solution: string;
  timestamp: string;
  published: boolean;
};

//Variable type
type varType = {
  name: string;
  type: string;
  options: option[];
  worldVar: boolean;
};

//Types of arithmetic operators
enum ArithOpType {
  add = "add",
  subtract = "subtract",
  multiply = "multiply",
  divide = "divide",
  mod = "mod", // modulo
  power = "power",
}

//Types of binary operators
enum BinOpType {
  eq = "eq", // is equal to
  neq = "neq", // is not equal to
  gt = "gt", // is greater than
  lt = "lt", // is less than
  gte = "gte", // is greater than or equal to
  lte = "lte", // is less than or equal to
  and = "and",
  or = "or",
  xor = "xor",
}

//Types of unary operators
enum UnOpType {
  not = "not",
}

//Classroom
type classroom = {
  classroomId: number;
  name: string;
};

// Standard variables that come with the levelmaker
type levelMakerInfo = {
  levelId: number;
  layout: levelData | null;
  answerBlocks: Set<string>;
  extraBlocks: Set<string>;
  remainingBlocks: Set<string>;
  constraints: { name: string; langid: string; value: boolean }[];
  name: string;
  html: string;
  gridTiles: gridCoordinates[];
  newLevel: boolean;
  seed: number;
  theme: string;
};

//Standard functions and variables that come with the world
type worldData = {
  constants: {
    name: string;
    type: string;
  }[];
  functions: worldFunc[];
  variables: worldVar[];
};

//A variable that is comes with the world
type worldVar = {
  name: string;
  type: string;
  readWrite: string;
};

//A function that comes with the world
type worldFunc = {
  name: string;
  paramNames: Record<string, string>;
  types: Record<string, string>;
  returnValue: string;
};

//Layout of a level
type levelData = {
  levelInstructions: instructionList;
  size: {
    width: number;
    height: number;
  };
  seed: number;
  start: startPoint[];
  end: endPoint[];
  path: tile[];
  interactables: tile[];
  obstacles: tile[];
  entities: entity[];
  nrOfMedals: string;
  theme: string;
};

//The starting point of a certain playable character
type startPoint = {
  rot: number;
  id: string;
  x: number;
  y: number;
};

//The end point of a certain playable character
type endPoint = {
  x: number;
  y: number;
};

//Non-movable square in the level grid
type tile = {
  type: string;
  x: number;
  y: number;
};

//Moving objects or interactables
type entity = {
  x: number;
  y: number;
  id: number;
  waypoints: {
    x: number;
    y: number;
  }[];
  followMode: string;
  type: string;
  rot: number;
};

//List of instructions
type instructionList = {
  needed: instruction[];
  extra: instruction[];
};

//Instruction
type instruction = {
  name: string;
};

//Option
type option = {
  name: string;
};

//Enitities that can be controlled via code
type playableCharacter = {
  x: number;
  y: number;
};

//Authentication tokens
type authTokens = {
  authToken: string;
  refreshToken: string;
};

//Special save for when editing or creating a level
type editorSave = {
  code: levelSave;
  tiles: tile[];
};

//Abstract syntax tree
type AST = {
  levelId: number;
  bundleId: number;
  compiledCode: string;
};

//Parser JSON types
type expression = {
  type: string;
  id?: string;
};

interface value extends expression {
  value: string | boolean | number;
}

interface constant extends expression {
  const: value;
}

interface variable extends expression {
  name: string;
}

interface ifelseStatement extends expression {
  condition: expression;
  ifCase: expression[];
  elseCase: expression[];
}

interface whileLoop extends expression {
  condition: expression;
  body: expression[];
}

interface functionDef extends expression {
  name: string;
  params: expression[];
  body: expression[];
}

interface functionCall extends expression {
  name: string;
  params: expression[];
}

interface assignment extends expression {
  varName: string;
  expression: expression;
  usesExplicitVar: boolean;
}

interface unaryOp extends expression {
  expression: expression;
  op: string;
}

interface brackets extends expression {
  inside: expression;
}

interface binaryOp extends expression {
  op: string;
  lhs: expression;
  rhs: expression;
}

//Instructions
type instructions = {
  instructions: instructionStep;
  statistics: {
    moved: string;
    deaths: string;
  };
  achievements: achievements;
  error: string | null;
  success: boolean;
  medal: string;
};

type operator = expression;

//Render instructions
//A single step containing all the relevant instructions in that step
type instructionStep = {
  instructions: instructionBase[];
};

//Base information needed for all instrentityID: string;uctions
type instructionBase = {
  name: string;
  id: string;
};

//Information about grid coordinates
type gridCoordinates = {
  x: number;
  y: number;
  object: string;
};

//Information about the bounds of the grid
type gridBounds = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
};

//Move entity instruction
interface moveInstruction extends instructionBase {
  entityID: string;
  targetPosition: [number, number];
}
//Start rotation instruction
interface rotInstruction extends instructionBase {
  entityID: string;
  degrees: number;
}
//Start sprite animation instruction
interface animInstruction extends instructionBase {
  entityID: string;
  anim: string;
}
//Set entity state instruction
interface stateInstruction extends instructionBase {
  entityID: string;
  state: string;
  value: string;
}

interface atInstruction extends instructionBase {
  env: [
    {
      name: string;
      value: string;
      type: string;
    }
  ];
}

//Structure of how the sprites are mapped
type spriteMap = {
  width: number;
  height: number;
  mapping: spriteMapping[];
};
//Where the sprite is
type spriteMapping = {
  id: string | number;
  x: number;
  y: number;
};

type wikiButton = {
  name: string;
  item: string;
};

export {
  levelState,
  tile,
  userInfo,
  authTokens,
  JWTToken,
  stats,
  levelStatsStudent,
  classroom,
  worldData,
  levelMakerInfo,
  worldFunc,
  worldVar,
  userPrefs,
  level,
  bundle,
  medals,
  achievement,
  achievements,
  bundleLevelSettings,
  levelSettings,
  levelSave,
  SavePacket,
  SaveObject,
  editorSave,
  PSDStackEntry,
  PSDStackEntryMove,
  PSDStackEntrySwitch,
  PSDStackEntryValue,
  AST,
  varType,
  option,
  ArithOpType,
  BinOpType,
  UnOpType,
  levelData,
  levelInfo,
  levelBundleProgress,
  entity,
  ifelseStatement,
  whileLoop,
  assignment,
  expression,
  variable,
  constant,
  value,
  unaryOp,
  brackets,
  binaryOp,
  functionCall,
  functionDef,
  operator,
  startPoint,
  endPoint,
  instructionList,
  instruction,
  instructions,
  instructionStep,
  instructionBase,
  gridCoordinates,
  gridBounds,
  moveInstruction,
  rotInstruction,
  animInstruction,
  stateInstruction,
  levelPreview,
  atInstruction,
  spriteMapping,
  wikiButton,
  spriteMap,
};
